import React, { createRef, useEffect, useState } from "react";
import moment from "moment";
import paths from "../../constants/paths";
import { SUCCESS, treatment_program } from "../../constants/common";
import {
  Box,
  Avatar,
  Link,
  Typography,
  Button,
  TextField,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { useHomePage } from "../../contexts/HomePage";
import { useBasicInfo } from "../../contexts/BasicInfo";
import { ReactComponent as MenuIcom } from "../../assets/Icons/menuIcom.svg";
import { ReactComponent as EditIcon } from "../../assets/Icons/edit-Post.svg";
import Reply from "./Reply";
import scrollTo from "../../helper/scrollTo";
import { validatePostContent } from "../../helper/Validations";
import { trackClickEvent } from "../../mixpanel";
import Tooltip from "@mui/material/Tooltip";
import { formatTimestamp } from "../../utils/formatTimestamp";
import { useAuth } from "../../contexts/Auth";
import { useGeneralProfile } from "../../contexts/Organization";

export default function Comment({
  item,
  setLoading,
  loading,
  post_id,
  editComment,
}) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [isInnerReplayOpen, setIsInnerReplayOpen] = useState(false);
  const [replyData, setReply] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);

  const {
    addReplyHandler,
    fetchPosts,
    handleLikeComment,
    handleUnlikeComment,
    getRepliesOfCommentHandler,
    updateReplyHandler,
    editReplayStatus,
    updateHomePagetate,
  } = useHomePage();
  const { data: info } = useBasicInfo();
  const { facilityInfo } = useGeneralProfile();
  const { account_type, isMentalHealthProfessional, permissions } = useAuth();

  const replyInput = createRef(null);

  const userInfo =
    account_type === treatment_program && !isMentalHealthProfessional()
      ? facilityInfo
      : info;

  const handleReply = () => {
    setShowReplyForm(!showReplyForm);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event) => {
    editComment({ comment: "" });
    setAnchorEl(event.currentTarget);
  };

  const handleLikeClick = (comment) => {
    try {
      if (comment.liked) {
        handleUnlikeComment({ comment_id: comment.id }, post_id);
        trackClickEvent("Comment Like Removed", {
          button: "Unlike",
          page: "Homepage",
          component: "Posts",
        });
      } else {
        handleLikeComment({ comment_id: comment.id }, post_id);
        trackClickEvent("Comment Liked", {
          button: "Like",
          page: "Homepage",
          component: "Posts",
        });
      }
    } catch (error) {}
  };

  const handleEditComment = () => {
    handleClose();
    editComment(item);
  };

  const onReplyTextChange = (ev) => {
    setReplyText(ev.target.value);
  };

  const editReply = (data) => {
    setReply(data);
    setReplyText(data.reply);
  };

  useEffect(() => {
    if (item?.replies_count > 0 && showReplyForm) {
      getRepliesOfCommentHandler(item.id, post_id);
    }
  }, [item.id, showReplyForm]);

  const isValid = () => {
    return replyText && validatePostContent(replyText);
  };

  async function handleKeypDown(ev) {
    if (ev.key === "Escape" && ev.keyCode === 27) {
      if (replyData?.id) {
        setReply(null);
        setReplyText("");
      }
    }

    if (ev.key === "Enter" && ev.keyCode === 13) {
      ev.preventDefault();
      if (!isValid()) {
        setIsInvalid(true);
        return;
      }
      if (!loading) {
        try {
          setLoading(true);
          if (replyData?.id) {
            await updateReplyHandler(
              { reply: replyText, reply_id: replyData.id },
              { comment_id: item.id, post_id },
            );
          } else {
            await addReplyHandler(
              {
                comment_id: item.id,
                reply: replyText,
                by_treatment_program: account_type === treatment_program,
              },
              post_id,
            );
          }
          trackClickEvent("Replied to Post", {
            page: "Homepage",
            component: "Post Card",
          });
          setLoading(false);
          setReplyText("");
        } catch (error) {
          setLoading(false);
        }
      }
    }
  }

  useEffect(() => {
    if (replyData?.id && replyInput?.current) {
      replyInput?.current?.focus();
      const element = replyInput?.current;
      scrollTo(element, 100);
    }
  }, [replyData]);

  useEffect(() => {
    if (editReplayStatus === SUCCESS) {
      updateHomePagetate("updateCommentStatus", "");
      setReplyText("");
      const replyRef = document.getElementById(`reply-${replyData?.id}`);
      if (replyRef) {
        scrollTo(replyRef, 100);
      }
      setReply(null);
    }
  }, [editReplayStatus]);

  return (
    <Box key={item.id} id={`comment-${item.id}`}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
        }}
      >
        <Box>
          <Avatar
            sx={{
              width: "40px",
              height: "40px",
            }}
            src={
              item?.by_treatment_program
                ? item?.user?.treatment_program_info?.treatment_program
                    ?.facility_image
                : item?.user?.user_profile_image
            }
          />
        </Box>
        <Box
          sx={{
            backgroundColor: "#F2F5F7",
            padding: "8px",
            marginLeft: "8px",
            width: "100%",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Link
              href={paths.userProfile(
                item?.user?.uuid,
                item?.user?.account_type === treatment_program
                  ? item?.user?.treatment_program_info?.treatment_program?.id
                  : "",
                item?.user?.account_type === treatment_program &&
                  item?.user?.roles_num === 1
                  ? treatment_program
                  : "user",
              )}
              onClick={() => {
                trackClickEvent("Profile Viewed", {
                  page: "Homepage",
                  component: "Comment",
                  button: "View Profile",
                });
              }}
              variant="body2"
              fontWeight={700}
              color="text.primary"
              textTransform="capitalize"
            >
              {item?.by_treatment_program
                ? item?.user?.treatment_program_info?.treatment_program
                    ?.facility_name
                : `${item?.user?.first_name} ${item?.user?.last_name}${
                    item?.user?.degrees?.length > 0
                      ? `, ${
                          item?.user?.degrees?.length > 0
                            ? item?.user?.degrees[0]?.degree.name
                            : ""
                        }`
                      : ""
                  }`}
            </Link>

            {item?.user?.id === info?.id && (
              <Box>
                <IconButton
                  aria-label="more"
                  id="long-button"
                  aria-controls={Boolean(anchorEl) ? "long-menu" : undefined}
                  aria-expanded={Boolean(anchorEl) ? "true" : undefined}
                  aria-haspopup="true"
                  onClick={handleClick}
                >
                  <MenuIcom />
                </IconButton>
                <Menu
                  id="long-menu"
                  MenuListProps={{
                    "aria-labelledby": "long-button",
                  }}
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  PaperProps={{
                    style: {
                      maxHeight: 48 * 4.5,
                      width: "20ch",
                    },
                  }}
                >
                  <MenuItem onClick={handleEditComment} disabled={!permissions?.post_comment_add}>
                    <EditIcon /> &nbsp; Edit Comment
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              wordWrap: "break-word",
              overflowWrap: "break-word",
              whiteSpace: "normal",
            }}
          >
            {item.comment}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ width: "48px" }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginLeft: "8px",
            width: "100%",
            pb: 1,
            pt: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Button
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                p: 0,
                minWidth: 0,
                color: item.liked ? "#0C8CE" : "#00000099",
                textAlign: "center",
                fontamily: "SF Pro Text",
                fontSize: "0.75rem",
                fontWeight: 600,
                lineHeight: "1rem",
              }}
              onClick={() => handleLikeClick(item)}
              disabled={!permissions?.post_comment_like}
            >
              Like {item?.likes_count > 0 ? ` (${item?.likes_count})` : ""}
            </Button>
            <Button
              variant="body2"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                p: 0,
                minWidth: 0,
                color: "#00000099",
                textAlign: "center",
                fontamily: "SF Pro Text",
                fontSize: "0.75rem",
                fontWeight: 600,
                lineHeight: "1rem",
              }}
              onClick={handleReply}
              disabled={!permissions?.post_comment_add}
            >
              Reply
              {item?.replies_count > 0 ? ` (${item?.replies_count})` : ""}
            </Button>
          </Box>
          <Tooltip
            title={formatTimestamp(item?.created_at).tooltipTime}
            placement="top"
          >
            <Typography
              style={{
                color: "#61676B",
                fontSize: "12px",
                marginRight: "8px",
                whiteSpace: "nowrap",
              }}
              fontWeight={400}
            >
              {formatTimestamp(item?.created_at).displayTime}
            </Typography>
          </Tooltip>
        </Box>
      </Box>
      {showReplyForm && (
        <Box sx={{ paddingLeft: "8%", paddingTop: "1%" }}>
          <Box>
            {item?.replies?.length > 0
              ? item?.replies.map((reply, index) => (
                  <Reply
                    data={reply}
                    comment={item}
                    post_id={post_id}
                    index={index}
                    setIsInnerReplayOpen={setIsInnerReplayOpen}
                    isInnerReplayOpen={isInnerReplayOpen}
                    setLoading={setLoading}
                    loading={loading}
                    fetchPosts={fetchPosts}
                    editReply={editReply}
                  />
                ))
              : null}
            {!isInnerReplayOpen && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1,
                  gap: 1,
                  mb: "1.5rem",
                  height: "2.25rem",
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                  }}
                  src={userInfo?.main_image}
                />
                <TextField
                  placeholder={`Reply to ${
                    item?.by_treatment_program
                      ? item?.user?.treatment_program_info?.treatment_program
                          ?.facility_name
                      : item?.user?.first_name
                  }`}
                  fullWidth
                  style={{ borderColor: "#0C8CE9" }}
                  disabled={loading || !permissions?.post_comment_add}
                  sx={{
                    input: {
                      py: 0.75,
                      px: 1,
                      fontSize: 14,
                      lineHeight: "22px",
                      maxHeight: "40px",
                      "&::placeholder": {
                        fontWeight: 700,
                      },
                    },
                  }}
                  onChange={onReplyTextChange}
                  name="comment"
                  value={replyText}
                  inputRef={replyInput}
                  onKeyDown={handleKeypDown}
                  error={isInvalid && !validatePostContent(replyText)}
                  helperText={
                    isInvalid &&
                    !validatePostContent(replyText) &&
                    "Please enter valid comment"
                  }
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
      {/* {index < data?.comments?.length - 1 && <Divider />} */}
    </Box>
  );
}
